export type LicenseStatus = "ok" | "grace" | "readonly";

export interface LicenseTokenPayload {
  orgId: string;
  seats: number;
  /** ISO expiry of paid period */
  expiresAt: string;
  /** ISO end of offline grace after expiry */
  graceEndsAt: string;
  /** HMAC / signed blob from control plane */
  signature: string;
}

export interface LicenseCheckResult {
  status: LicenseStatus;
  token: LicenseTokenPayload | null;
  message: string;
  needsName?: boolean;
  user?: { userId: string; displayName: string };
}

const STORAGE_KEY = "minproduktion.license";
const LEGACY_STORAGE_KEY = "supermanus.license";

type LicenseCacheRecord = {
  licenseKey?: string;
  token: LicenseTokenPayload;
};

function isTokenPayload(value: unknown): value is LicenseTokenPayload {
  return Boolean(
    value &&
      typeof value === "object" &&
      "signature" in value &&
      "expiresAt" in value,
  );
}

function readCacheRecord(): LicenseCacheRecord | null {
  try {
    const raw =
      localStorage.getItem(STORAGE_KEY) ??
      localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === "object" &&
      "token" in parsed &&
      isTokenPayload((parsed as LicenseCacheRecord).token)
    ) {
      return parsed as LicenseCacheRecord;
    }
    if (isTokenPayload(parsed)) {
      return { token: parsed };
    }
    return null;
  } catch {
    return null;
  }
}

export function loadCachedLicense(): LicenseTokenPayload | null {
  return readCacheRecord()?.token ?? null;
}

export function cacheLicense(
  token: LicenseTokenPayload,
  licenseKey?: string,
): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ licenseKey, token }));
}

export function clearCachedLicense(): void {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(LEGACY_STORAGE_KEY);
}

function cachedTokenForKey(licenseKey: string): LicenseTokenPayload | null {
  const record = readCacheRecord();
  if (!record) return null;
  if (record.licenseKey && record.licenseKey !== licenseKey) return null;
  return record.token;
}

export function evaluateLicense(
  token: LicenseTokenPayload | null,
  now = Date.now(),
): LicenseCheckResult {
  if (!token) {
    return {
      status: "readonly",
      token: null,
      message: "No license. Enter a license key when online.",
    };
  }
  const expires = new Date(token.expiresAt).getTime();
  const graceEnds = new Date(token.graceEndsAt).getTime();
  if (now <= expires) {
    return { status: "ok", token, message: "License active." };
  }
  if (now <= graceEnds) {
    return {
      status: "grace",
      token,
      message: "Subscription expired — offline grace period active.",
    };
  }
  return {
    status: "readonly",
    token,
    message: "License expired. Renew to continue editing.",
  };
}

function licenseCheckUrl(controlPlaneUrl: string): string {
  const root = controlPlaneUrl.replace(/\/$/, "");
  if (root === "/api" || root.endsWith("/api")) {
    return `${root}/license/check`;
  }
  return `${root}/v1/license/check`;
}

function isRejectedLicenseResponse(status: number, code: string | null): boolean {
  return (
    status === 400 ||
    status === 401 ||
    status === 403 ||
    code === "invalid_license"
  );
}

async function readErrorCode(res: Response): Promise<string | null> {
  try {
    const body = (await res.clone().json()) as { code?: string };
    return typeof body.code === "string" ? body.code : null;
  } catch {
    return null;
  }
}

function rejectedLicenseResult(): LicenseCheckResult {
  clearCachedLicense();
  return {
    status: "readonly",
    token: null,
    message: "Invalid license key.",
  };
}

function fallbackFromCache(
  licenseKey: string,
  suffix: string,
): LicenseCheckResult {
  const evaluated = evaluateLicense(cachedTokenForKey(licenseKey));
  if (evaluated.status === "ok" || evaluated.status === "grace") {
    return {
      ...evaluated,
      message: `${evaluated.message} ${suffix}`,
    };
  }
  return {
    status: "readonly",
    token: null,
    message: "License check failed and no valid cached license.",
  };
}

export async function refreshLicense(
  controlPlaneUrl: string,
  licenseKey: string,
): Promise<LicenseCheckResult> {
  try {
    const res = await fetch(licenseCheckUrl(controlPlaneUrl), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ licenseKey }),
    });
    if (res.ok) {
      const token = (await res.json()) as LicenseTokenPayload;
      cacheLicense(token, licenseKey);
      return evaluateLicense(token);
    }
    const code = await readErrorCode(res);
    if (isRejectedLicenseResponse(res.status, code)) {
      return rejectedLicenseResult();
    }
    return fallbackFromCache(licenseKey, "(could not reach license server)");
  } catch {
    return fallbackFromCache(licenseKey, "(could not reach license server)");
  }
}

async function clearServerSession(apiBase: string): Promise<void> {
  try {
    await fetch(`${apiBase.replace(/\/$/, "")}/session`, {
      method: "DELETE",
      credentials: "include",
    });
  } catch {
    /* ignore */
  }
}

export async function openApiSession(
  apiBase: string,
  opts: { licenseKey: string; displayName?: string; userId?: string },
): Promise<LicenseCheckResult> {
  try {
    const res = await fetch(`${apiBase.replace(/\/$/, "")}/session`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        licenseKey: opts.licenseKey,
        ...(opts.displayName?.trim()
          ? { displayName: opts.displayName.trim() }
          : {}),
      }),
    });
    if (res.ok) {
      const data = (await res.json()) as {
        token?: LicenseTokenPayload;
        user?: { userId: string; displayName: string };
      };
      if (data.token) {
        cacheLicense(data.token, opts.licenseKey);
        const evaluated = evaluateLicense(data.token);
        return {
          ...evaluated,
          user:
            data.user?.userId && data.user.displayName
              ? {
                  userId: data.user.userId,
                  displayName: data.user.displayName,
                }
              : undefined,
        };
      }
      return refreshLicense(apiBase, opts.licenseKey);
    }
    const code = await readErrorCode(res);
    if (code === "name_required") {
      return {
        status: "readonly",
        token: null,
        message: "Name required to activate this license.",
        needsName: true,
      };
    }
    if (isRejectedLicenseResponse(res.status, code)) {
      await clearServerSession(apiBase);
      return rejectedLicenseResult();
    }
    return refreshLicense(apiBase, opts.licenseKey);
  } catch {
    return refreshLicense(apiBase, opts.licenseKey);
  }
}

/** Periodic check helper — call when online. */
export async function ensureLicense(
  controlPlaneUrl: string,
  licenseKey: string | null,
): Promise<LicenseCheckResult> {
  if (!licenseKey) {
    return evaluateLicense(loadCachedLicense());
  }
  try {
    return await refreshLicense(controlPlaneUrl, licenseKey);
  } catch {
    return fallbackFromCache(licenseKey, "(offline)");
  }
}
